import { Component } from '@angular/core';
import { firstValueFrom, map, Observable, switchMap, tap } from 'rxjs';
import { DashboardService } from '../services/dashboard.service';
import { ActivatedRoute, Router } from '@angular/router';
import { Widget, WidgetSelection, WidgetsGroup } from '../models/dashboard.models';
import { MarkantSelectOption } from '@markant-platform/shared/util-helpers';
import { TranslocoService } from '@jsverse/transloco';

@Component({
  selector: 'markant-platform-dashboard-widget-selection',
  templateUrl: './dashboard-widget-selection.component.html',
  styleUrls: ['./dashboard-widget-selection.component.scss'],
})
export class DashboardWidgetSelectionComponent {
  groupedWidgets$: Observable<WidgetsGroup[]>;

  preselectedWidgets$: Observable<WidgetSelection[]>;
  selectedWidgets: WidgetSelection[] = [];
  protected widgetGroupOptions: MarkantSelectOption[] = [];

  filteredWidgetGroupId = 'all';
  preFilteredWidgetGroup: string;

  constructor(
    private dashboardService: DashboardService,
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private readonly translocoService: TranslocoService
  ) {
    this.groupedWidgets$ = this.dashboardService
      .getUserRoles$()
      .pipe(
        switchMap(userRoles => dashboardService.getAvailableWidgetsGrouped$(userRoles)),
        tap(() => {
          const fragment = this.activatedRoute.snapshot.fragment;
          if (fragment) {
            setTimeout(() => {
              this.scrollToGroup(fragment);
            }, 100);
          }
        })
      )
      .pipe(
        tap((widgets: WidgetsGroup[]) => {
          this.widgetGroupOptions = [
            {
              value: 'all',
              representation: this.translocoService.translate('dashboard.selection.filter.showAll'),
            },
            ...widgets.map((widget: WidgetsGroup) => ({
              value: widget.groupId,
              representation: widget.groupName,
            })),
          ];
        })
      );
    this.preselectedWidgets$ = this.dashboardService.getVisibleWidgets$().pipe(
      map(widgets => {
        return widgets.map(widget => ({
          widgetId: widget.id,
          widgetType: widget.widgetType,
          cartridgeId: widget.cartridgeId,
          gridPositionX: widget.gridPositionX,
          gridPositionY: widget.gridPositionY,
        }));
      })
    );
    this.preFilteredWidgetGroup = this.activatedRoute.snapshot.queryParams['preFilteredCartridge'];

    if (this.preFilteredWidgetGroup) {
      this.filterWidgetGroup({ detail: this.preFilteredWidgetGroup } as unknown as Event);
    }
  }

  async saveSelected(preselectedWidgets: WidgetSelection[]): Promise<void> {
    await firstValueFrom(this.dashboardService.updateVisibleWidgets(preselectedWidgets.concat(this.selectedWidgets)));
    await this.router.navigateByUrl('/');
  }

  filterWidgetGroup(event: Event): void {
    this.filteredWidgetGroupId = (event as CustomEvent).detail;
  }

  selectWidget(widget: Widget, preselectedWidgets: WidgetSelection[], variantId?: string | undefined | null): void {
    const widgetVariantId = variantId ?? widget.cartridgeId;
    if (preselectedWidgets.find(({ widgetId: id, cartridgeId: cId }) => id === widget.id && cId == widgetVariantId)) {
      return;
    }

    const index = this.selectedWidgets.findIndex(
      ({ widgetId: id, cartridgeId: cId }) => id === widget.id && cId == widgetVariantId
    );
    if (index > -1) {
      this.selectedWidgets.splice(index, 1);
    } else {
      this.selectedWidgets.push({
        widgetId: widget.id,
        widgetType: widget.widgetType,
        cartridgeId: widgetVariantId,
        gridPositionX: widget.gridPositionX,
        gridPositionY: widget.gridPositionY,
      });
    }
  }

  private scrollToGroup(id: string): void {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start', inline: 'nearest' });
  }
}
